import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Container, Row, Col, Form } from "react-bootstrap";
import swal from "sweetalert";
import { TableSkeletonRows } from "../loader";
import { aiService } from "../../service/api.service";
import { TablePagination } from "../common/TablePagination";
import { EmptyTableState } from "../common/EmptyTableState";
import { StatusBadge } from "../common/StatusBadge";
import AssignNumberModal from "./AssignNumberModal";
import { StyledTable, StyledTableHeader, StyledTableCell, StyledTableRow } from "../common/StyledTable";

const STATUS_OPTIONS = ["Assigned", "Not Assigned"];

export default function AssignNumbers() {
  const location = useLocation();
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [agents, setAgents] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(() => {
    const status = new URLSearchParams(location.search).get("status");
    return STATUS_OPTIONS.includes(status) ? status : "";
  });
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [modalAgent, setModalAgent] = useState(null);
  const page_size = 10;

  const assignedParam =
    statusFilter === "Assigned" ? "true" : statusFilter === "Not Assigned" ? "false" : "";

  const getAgentList = useCallback(async () => {
    setLoader(true);
    try {
      const res = await aiService.getAgents({
        all: true,
        page,
        page_size,
        search: debouncedSearch,
        assigned: assignedParam,
      });
      setAgents(res?.data?.data || []);
      setTotalItems(res?.data?.total_count || 0);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      if (error?.response?.status === 401) {
        swal({ icon: "error", text: "Unauthorized", button: "OK" }).then(() => {
          localStorage.clear();
          window.location.href = "/";
        });
      } else {
        swal({ icon: "error", text: "Failed to load agents." });
      }
    }
  }, [page, debouncedSearch, assignedParam]);

  useEffect(() => {
    getAgentList();
  }, [getAgentList]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    const openAgentId = location.state?.openAgentId;
    if (!openAgentId || !agents.length) return;
    const target = agents.find((a) => a._id === openAgentId);
    if (target) setModalAgent(target);
    navigate(location.pathname + location.search, { replace: true, state: {} });
  }, [agents, location.state, location.pathname, location.search, navigate]);

  useEffect(() => {
    const status = new URLSearchParams(location.search).get("status");
    setStatusFilter(STATUS_OPTIONS.includes(status) ? status : "");
    setPage(1);
  }, [location.search]);

  function handlePageChange(pageNumber) {
    setPage(pageNumber);
  }

  function handleStatusFilterChange(value) {
    setStatusFilter(value);
    setPage(1);
  }

  const openModal = (agent) => setModalAgent(agent);
  const closeModal = () => setModalAgent(null);

  return (
    <div className="dashboard-main-area company-list-area">
      <Container fluid>
        <div className="dashboard-area-heading management-heading">
          <div className="under-content-section">
            <div>
              <h1 className="company-page-title">Assign Numbers</h1>
              <h5 className="company-page-subtitle">
                {agents.length} of {totalItems} agents
              </h5>
            </div>
          </div>
          <Row className="pb-4 justify-content-center">
            <Col md={12}>
              <div className="premium-filter-card">
                <div className="search-top-right">
                  <i className="fa fa-search" aria-hidden="true"></i>
                  <Form.Control
                    placeholder="Search by agent name…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <select
                  className="filter-select"
                  aria-label="Status"
                  value={statusFilter}
                  onChange={(e) => handleStatusFilterChange(e.target.value)}
                >
                  <option value="">Status</option>
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="audit-request-box premium-company-card">
                <StyledTable responsive="sm" className="UserListTable premium-user-list-table all-agent-list-table">
                  <thead>
                    <StyledTableRow>
                      <StyledTableHeader>Agent Name</StyledTableHeader>
                      <StyledTableHeader>Owned By</StyledTableHeader>
                      <StyledTableHeader>Status</StyledTableHeader>
                      <StyledTableHeader>Actions</StyledTableHeader>
                    </StyledTableRow>
                  </thead>
                  <tbody>
                    {loader ? (
                      <TableSkeletonRows rows={page_size} columns={4} />
                    ) : (
                      agents.length > 0 ? agents.map((a) => (
                        <StyledTableRow key={a._id}>
                          <StyledTableCell>{a.name}</StyledTableCell>
                          <StyledTableCell>
                            <div className="agent-owner-cell">
                              <span>{a.owner_name || a.owner_email || "Unknown user"}</span>
                              {a.owner_name && a.owner_email ? <small>{a.owner_email}</small> : null}
                            </div>
                          </StyledTableCell>
                          <StyledTableCell>
                            {a.phone ? (
                              <StatusBadge label="Assigned" tone="success" />
                            ) : (
                              <StatusBadge label="Not Assigned" tone="muted" />
                            )}
                          </StyledTableCell>
                          <StyledTableCell>
                            <div className="agent-action-buttons">
                              <button
                                type="button"
                                className="btn btn-sm btn-light"
                                title="Manage Phone Number"
                                onClick={() => openModal(a)}
                              >
                                <i className="fa fa-phone" aria-hidden="true"></i>
                              </button>
                            </div>
                          </StyledTableCell>
                        </StyledTableRow>
                      )) : (
                        <EmptyTableState colSpan={4} />
                      )
                    )}
                  </tbody>
                </StyledTable>
                <TablePagination
                  activePage={page}
                  itemsCountPerPage={page_size}
                  totalItemsCount={totalItems}
                  onChange={handlePageChange}
                />
              </div>
            </Col>
          </Row>
        </div>
      </Container>

      <AssignNumberModal
        show={!!modalAgent}
        handleClose={closeModal}
        agent={modalAgent}
        onSuccess={getAgentList}
      />
    </div>
  );
}
